<template>
  <!-- AVAILABLE: a small card at the bottom, never a modal — the board stays usable. -->
  <transition name="aup-slide">
    <section v-if="showSoft || showReady" class="aup" role="status">
      <img v-if="appIcon" :src="appIcon" class="aup-icon" alt="" />
      <q-icon v-else name="mdi-rocket-launch-outline" size="28px" class="aup-icon aup-icon--glyph" />
      <div class="aup-text">
        <div class="aup-title">{{ showReady ? $t('appUpdate.readyTitle') : $t('appUpdate.softTitle') }}</div>
        <div class="aup-sub">{{ showReady ? $t('appUpdate.readySub') : $t('appUpdate.softSub') }}</div>
      </div>
      <div class="aup-row">
        <template v-if="showReady">
          <button type="button" class="aup-btn aup-btn--ghost" @click="showReady = false">{{ $t('appUpdate.later') }}</button>
          <button type="button" class="aup-btn aup-btn--go" @click="restartNow">{{ $t('appUpdate.restart') }}</button>
        </template>
        <template v-else>
          <button type="button" class="aup-btn aup-btn--ghost" @click="later">{{ $t('appUpdate.later') }}</button>
          <button type="button" class="aup-btn aup-btn--go" @click="updateNow">{{ $t('appUpdate.update') }}</button>
        </template>
      </div>
    </section>
  </transition>

  <!-- REQUIRED: only below the operator's minimum version. Kind words, one button. -->
  <q-dialog :model-value="showForce" persistent transition-show="fade">
    <section class="aup-force">
      <img v-if="appIcon" :src="appIcon" class="aup-force-icon" alt="" />
      <q-icon v-else name="mdi-rocket-launch-outline" size="56px" color="primary" />
      <div class="aup-force-title">{{ $t('appUpdate.forceTitle') }}</div>
      <div class="aup-force-sub">{{ $t('appUpdate.forceSub') }}</div>
      <button type="button" class="aup-btn aup-btn--go aup-btn--wide" @click="updateRequired">
        {{ $t('appUpdate.forceBtn') }}
      </button>
    </section>
  </q-dialog>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import { useAppUpdate } from '../composables/useAppUpdate'
import { flavorDefaultLogo } from 'src/flavors/settings'

const $q = useQuasar()
const { t } = useI18n({ useScope: 'global' })
const { showSoft, showForce, showReady, downloading, later, updateNow, restartNow, updateRequired } = useAppUpdate()
/** The flavor's own app icon — the same one on the home screen, so it reads as "this app". */
const appIcon = flavorDefaultLogo(process.env.BUSINESS_TYPE || 'table')

// Android downloads in the background: one quiet line so "Update" visibly did something.
watch(downloading, (on) => {
  if (on) $q.notify({ message: t('appUpdate.downloading'), icon: 'mdi-download', color: 'grey-9', position: 'bottom', timeout: 4000 })
})
</script>

<style scoped>
.aup { position: fixed; z-index: 6000; left: 50%; bottom: calc(16px + env(safe-area-inset-bottom)); transform: translateX(-50%); width: 420px; max-width: calc(100vw - 24px); box-sizing: border-box; display: grid; grid-template-columns: auto 1fr; gap: 6px 12px; align-items: center; padding: 12px 14px; border-radius: 18px; background: #fff; box-shadow: 0 12px 36px rgba(0, 20, 40, .22); }
body.body--dark .aup { background: var(--dd-surface, #15222d); }
.aup-icon { width: 40px; height: 40px; border-radius: 10px; object-fit: cover; grid-row: span 1; }
.aup-icon--glyph { color: var(--q-primary, #003250); }
.aup-title { font-size: 15px; font-weight: 800; line-height: 1.25; color: var(--q-primary, #003250); }
.aup-sub { font-size: 13px; line-height: 1.35; color: rgba(0, 50, 80, .62); }
.aup-row { grid-column: 1 / -1; display: flex; justify-content: flex-end; gap: 6px; }
.aup-btn { border: 0; border-radius: 999px; font: inherit; font-weight: 700; font-size: 14px; padding: 7px 18px; cursor: pointer; }
.aup-btn--go { background: #73be00; color: #fff; }
.aup-btn--ghost { background: none; color: rgba(0, 50, 80, .6); }
.aup-btn--wide { width: 100%; padding: 12px 18px; font-size: 16px; margin-top: 6px; }
.aup-force { width: 380px; max-width: 92vw; box-sizing: border-box; padding: 28px 24px 22px; border-radius: 24px; background: #fff; display: grid; gap: 10px; justify-items: center; text-align: center; }
body.body--dark .aup-force { background: var(--dd-surface, #15222d); }
.aup-force-icon { width: 72px; height: 72px; border-radius: 18px; object-fit: cover; box-shadow: 0 6px 16px rgba(0, 50, 80, .22); }
.aup-force-title { font-size: 20px; font-weight: 800; color: var(--q-primary, #003250); }
.aup-force-sub { font-size: 14px; line-height: 1.5; color: rgba(0, 50, 80, .65); max-width: 40ch; }
body.body--dark .aup-title, body.body--dark .aup-force-title { color: var(--dd-text, #e6f0f7); }
body.body--dark .aup-sub, body.body--dark .aup-force-sub, body.body--dark .aup-btn--ghost { color: var(--dd-text-muted, #93a6b6); }
.aup-slide-enter-active, .aup-slide-leave-active { transition: transform .3s ease, opacity .3s ease; }
.aup-slide-enter-from, .aup-slide-leave-to { transform: translate(-50%, 24px); opacity: 0; }
@media (prefers-reduced-motion: reduce) { .aup-slide-enter-active, .aup-slide-leave-active { transition: none; } }
</style>
